import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { submitOffline } from '../../services/offlineHelpers';
import { addAnimalToCache } from '../../services/offlineDB';

const RegistrarAnimalForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    VID: '', IDE: '', sexo: 'hembra', fecha_nacimiento: '',
    raza: 'Brangus', categoria: 'ternero', propietario: '', observaciones: '',
    peso_inicial: '', fecha_pesaje: '',
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const payload = { ...formData, establecimiento_id: user.establecimiento_id };
      // Convert peso_inicial to number if present
      if (payload.peso_inicial) {
        payload.peso_inicial = parseFloat(payload.peso_inicial);
      } else {
        delete payload.peso_inicial;
      }
      if (!payload.fecha_pesaje) delete payload.fecha_pesaje;
      if (!payload.fecha_nacimiento) delete payload.fecha_nacimiento;
      const offlineId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      if (!navigator.onLine) payload.offline_id = offlineId;
      const result = await submitOffline('/api/animales/', payload);

      if (result.online) {
        setSuccess('Animal registrado exitosamente.');
      } else {
        setSuccess('Animal guardado localmente. Se sincronizara al volver a tener internet.');
        // Add to local cache for offline search
        await addAnimalToCache({
          ...payload,
          id: offlineId,
          estado: 'activo',
        });
      }

      setFormData({
        VID: '', IDE: '', sexo: 'hembra', fecha_nacimiento: '',
        raza: 'Brangus', categoria: 'ternero', propietario: '', observaciones: '',
        peso_inicial: '', fecha_pesaje: '',
      });

      if (onSuccess) setTimeout(onSuccess, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar animal');
    } finally {
      setLoading(false);
    }
  };

  const isOffline = !navigator.onLine;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Registrar Nuevo Animal</h2>
        <div className="flex items-center space-x-2">
          {isOffline && <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold" data-testid="offline-badge">MODO CAMPO</span>}
          {onCancel && (
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" data-testid="close-form-btn">&times;</button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="form-error">
          {error}
        </div>
      )}

      {success && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${isOffline ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-green-50 border border-green-200 text-green-700'}`} data-testid="form-success">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-animal-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">VID (Caravana Visual) *</label>
            <input type="text" name="VID" value={formData.VID} onChange={handleChange} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: 1234" data-testid="animal-vid" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">IDE (RFID) - Opcional</label>
            <input type="text" name="IDE" value={formData.IDE} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono" placeholder="982123456789012" data-testid="animal-ide" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sexo *</label>
            <select name="sexo" value={formData.sexo} onChange={handleChange} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="animal-sexo">
              <option value="hembra">Hembra</option>
              <option value="macho">Macho</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Nacimiento - Opcional</label>
            <input type="date" name="fecha_nacimiento" value={formData.fecha_nacimiento} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="animal-fecha" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Raza</label>
            <input type="text" name="raza" value={formData.raza} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: Brangus" data-testid="animal-raza" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Categoria Inicial</label>
            <select name="categoria" value={formData.categoria} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="animal-categoria">
              <option value="ternero">Ternero</option>
              <option value="desmamante">Desmamante</option>
              <option value="vaquilla">Vaquilla</option>
              <option value="novillo">Novillo</option>
              <option value="vaca">Vaca</option>
              <option value="toro">Toro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Propietario</label>
            <input type="text" name="propietario" value={formData.propietario} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Nombre del propietario" data-testid="animal-propietario" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Peso Inicial (kg) - Opcional</label>
            <input type="number" name="peso_inicial" value={formData.peso_inicial} onChange={handleChange} step="0.1" min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: 350" data-testid="animal-peso" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha del Pesaje</label>
            <input type="date" name="fecha_pesaje" value={formData.fecha_pesaje} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" data-testid="animal-fecha-pesaje" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
          <textarea name="observaciones" value={formData.observaciones} onChange={handleChange} rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Notas adicionales..." data-testid="animal-obs" />
        </div>
        <div className="flex justify-end space-x-3 pt-4">
          {onCancel && (
            <button type="button" onClick={onCancel} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-testid="cancel-btn">
              Cancelar
            </button>
          )}
          <button type="submit" disabled={loading}
            className={`px-6 py-2 text-white rounded-lg disabled:opacity-50 ${isOffline ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
            data-testid="submit-animal-btn">
            {loading ? 'Guardando...' : isOffline ? 'Guardar Local' : 'Registrar Animal'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrarAnimalForm;
